/**
 * The trial balance on screen (W3-17 (8d)): the words and choices its panel shows.
 *
 * The service works everything out — each row's month, what the documents report of it, why it
 * holds — and serves it on Review's read (`trial_balance`). This only says it: how a document
 * reports, which rows a sheet may be linked to, and which rows a person may mark. Pure.
 */

import type {
  TrialBalanceDocument,
  TrialBalanceRow,
  TrialBalanceRowRef,
} from './api'

/** A trial balance is its own kind of document (8c); Raw Data and the preview show its panel. */
export const TRIAL_BALANCE = 'trial_balance'

export function isTrialBalance(doc: { doc_type?: string | null }): boolean {
  return doc.doc_type === TRIAL_BALANCE
}

/** How one of the month's documents tells the trial balance what it reports (D-94). */
export function reportsWords(doc: TrialBalanceDocument): string {
  switch (doc.reports) {
    case 'by_line':
      return 'Its lines name their ledger, so each line comes off the row it names.'
    case 'linked':
      return `Linked to ${doc.linked.map(refWords).join(', ')}: its lines come off ${
        doc.linked.length === 1 ? 'that row' : 'those rows'
      }.`
    case 'unlinked':
      return 'It names no ledger and is linked to no row, so every row it might report holds. Link it to the rows it reports, or reject it.'
    case 'not_a_sheet':
      return 'Read from a PDF or a platform report, so it names no ledger and cannot be linked. If it reports a row, take that row’s line out.'
    case 'awaits_columns':
      return 'Its columns are not bound yet, so what it reports is not known. Bind it on Upload Data.'
    default:
      return doc.reports
  }
}

/** A linked row in words: a ledger's code, or a carve-out's name. */
export function refWords(ref: Record<string, string>): string {
  return ref.code ?? ref.name ?? ''
}

/** Only a sheet read through a layout can be linked; the link is kept on that layout. */
export function linkable(doc: TrialBalanceDocument): boolean {
  return doc.profile_id !== null && (doc.reports === 'linked' || doc.reports === 'unlinked')
}

/** A row as a link names it: a ledger by its code, a carve-out (no code) by its name. */
export function refOf(row: TrialBalanceRow): TrialBalanceRowRef {
  return row.code ? { code: row.code } : { name: row.name }
}

function fold(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase()
}

/** Whether a document's link names this row, by the service's own rule (`RowRef.matches`). */
export function linksTo(doc: TrialBalanceDocument, row: TrialBalanceRow): boolean {
  return doc.linked.some((ref) =>
    ref.code !== undefined
      ? row.code !== null && row.code.trim() === ref.code
      : row.code === null && fold(row.name) === fold(ref.name ?? ''),
  )
}

/** A TAXABLE carve-out may be marked as not carved from exempt income (D-95); nothing else. */
export function markable(row: TrialBalanceRow): boolean {
  return row.carve_out && row.remark === 'TAXABLE'
}

/** The row's rate as a person reads it, with the cell as written when that differs (D-95). */
export function rateWords(row: TrialBalanceRow): string {
  if (row.rate === null) return row.rate_written ? `none (“${row.rate_written}”)` : '—'
  const shown = `${row.rate}%`
  return row.rate_written && row.rate_written !== String(row.rate) && row.rate_written !== shown
    ? `${shown} (written “${row.rate_written}”)`
    : shown
}

/** A remark as the trial balance gives it; none is said, never left blank. */
export function remarkWords(remark: string | null): string {
  return remark ?? 'No GST remark'
}
